// 接口基地址：开发环境读取 .env.development 的 VUE_APP_BASE_API（本地后端），
// 生产构建读取 .env.production；未配置时兜底为线上地址
const BASE_URL = process.env.VUE_APP_BASE_API || 'https://web.daohan.com.cn'

/**
 * 获取存储的token
 * @returns {string|null} - 返回存储的token或null
 */
function getToken() {
  return localStorage.getItem('token')
}

/**
 * 基础请求函数
 * @param {string} url - 请求地址
 * @param {Object} options - 请求配置
 * @returns {Promise<Object>} - 返回处理后的数据
 */
async function request(url, options = {}) {
  // 构建完整URL
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`
  
  // 设置默认请求头
  const defaultHeaders = {}
  
  // 对于POST请求，必须添加Content-Type
  if (options.method === 'POST') {
    defaultHeaders['Content-Type'] = 'application/json'
  }
  
  // 添加认证token，与Postman保持完全一致
  const token = getToken()
  if (token) {
    defaultHeaders['token'] = token
    console.log('使用token:', token);
  }
  
  // 合并选项
  const fetchOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers
    }
  }
  
  // 打印详细的请求信息用于调试
  console.log(`===DEBUG REQUEST===: ${options.method || 'GET'} ${fullUrl}`);
  console.log('Headers:', fetchOptions.headers);
  if (options.body) {
    console.log('Request Body:', options.body);
  }
  
  try {
    // 发送请求
    const response = await fetch(fullUrl, fetchOptions)
    
    // 判断是否是500服务器错误
    if (response.status === 500) {
      console.error(`===重要错误===: 服务器返回500错误 (${fullUrl})`);
      // 尝试获取错误详情
      const errorText = await response.text();
      console.error('服务器错误详情:', errorText.substring(0, 500));
      
      // 尝试模拟正常响应避免阻断用户
      return {
        code: 1,
        msg: "服务器临时不可用，但您可以继续使用其他功能",
        data: null
      };
    }
    
    // 检查请求是否成功
    if (!response.ok) {
      // 根据状态码给出更友好的错误提示
      let errorMessage;
      
      switch(response.status) {
        case 401:
          errorMessage = '未授权，请重新登录';
          // 自动清除凭证
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          break;
        case 403:
          errorMessage = '没有权限访问该资源';
          break;
        case 404:
          errorMessage = '请求的资源不存在';
          break;
        case 500:
          errorMessage = '服务器内部错误，请稍后再试';
          break;
        default:
          errorMessage = `请求失败(${response.status})`;
      }
      
      // 如果是非登录的页面，对于500错误返回一个模拟响应
      if (response.status === 500 && !url.includes('login')) {
        return {
          code: 1,
          msg: "服务器暂时不可用，但您可以继续使用基本功能",
          data: null
        };
      }
      
      throw new Error(errorMessage);
    }
    
    // 解析响应数据
    const data = await response.json()
    
    // 检查业务状态码
    if (data.code === 401) {
      // Token过期或无效，清除本地存储
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      const authError = new Error('登录已过期，请重新登录')
      authError.isBusinessError = true
      throw authError
    } else if (data.code !== 1 && data.code < 1000) {
      console.error('API错误:', data.msg || '未知错误')
      // 业务错误必须抛出，由调用方处理；
      // code >= 1000（如青少年模式限制码 1001-1005）原样透传给组件。
      const businessError = new Error(data.msg || '未知错误')
      businessError.isBusinessError = true
      businessError.businessCode = data.code
      businessError.data = data.data
      throw businessError
    }
    
    return data
  } catch (error) {
    console.error('请求错误:', error)
    
    // 业务错误（code !== 1）不能吞掉，必须让调用方感知
    if (error && error.isBusinessError) {
      throw error
    }
    
    // 为了防止整个应用崩溃，对于非认证相关的网络/HTTP失败返回一个默认响应
    if (!url.includes('login') && !url.includes('register')) {
      return {
        code: 1,
        msg: "请求出错，但您可以继续使用基本功能",
        data: null
      };
    }
    
    throw error
  }
}

// 导出通用请求方法
export default {
  baseUrl: BASE_URL,
  get: (url, params = {}) => {
    // 处理查询参数
    const queryString = Object.keys(params)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&')
    
    const fullUrl = queryString ? `${url}?${queryString}` : url
    
    return request(fullUrl, {
      method: 'GET'
    })
  },
  
  post: (url, data = {}) => {
    return request(url, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },

  /**
   * 文件上传（multipart/form-data）
   * @param {string} url - 上传接口地址
   * @param {FormData} formData - 表单数据
   * @returns {Promise<Object>} - 返回 {code, msg, data:{url, fullurl}}
   */
  async upload(url, formData) {
    const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`
    const headers = {}
    const token = getToken()
    if (token) {
      headers['token'] = token
    }
    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: formData
      })
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
        }
        throw new Error(`上传失败(${response.status})`)
      }
      const data = await response.json()
      if (data.code === 401) {
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        const authError = new Error('登录已过期，请重新登录')
        authError.isBusinessError = true
        throw authError
      }
      if (data.code !== 1) {
        const businessError = new Error(data.msg || '上传失败')
        businessError.isBusinessError = true
        throw businessError
      }
      return data
    } catch (error) {
      if (error && error.isBusinessError) {
        throw error
      }
      throw new Error((error && error.message) || '上传失败，请重试')
    }
  }
} 
